import React from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import BusinessAccountMultiStepForm, {
  BusinessFormValues,
} from "./BussinessAccounRegister";
import useBusinessInfo from "../../../hooks/useBusiness";

/**
 * API helper to register a business account
 */
const postBusinessAccount = async (values: BusinessFormValues) => {
  console.group("📦 Submitting Business Account Data");
  console.table({
    "Business Type": values.businessType,
    "Business Name": values.businessName,
    Country: values.country,
    Address: values.address,
    "Registration No.": values.registrationNumber || "N/A",
    "Contact Name": values.contactName,
    "Contact Phone": `${values.contactCountryCode}${values.contactPhone}`,
    "Business Email": values.businessEmail,
    "Email Domain": values.emailDomain,
    Password: "********",
  });
  console.groupEnd();

  // ✅ Convert to URL-encoded format as required by API
  const formData = new URLSearchParams();
  formData.append("business_type", values.businessType);
  formData.append("business_name", values.businessName);
  formData.append("country", values.country);
  formData.append("address", values.address);
  formData.append("registration_number", values.registrationNumber || "");
  formData.append("contact_name", values.contactName);
  formData.append("contact_country_code", values.contactCountryCode);
  formData.append("contact_number", values.contactPhone); // ✅ Correct field name
  formData.append("business_email", values.businessEmail); // ✅ Correct field name
  formData.append("email_domain", values.emailDomain);
  formData.append("password", values.password);

  const response = await axios.post(
    "https://api.taasnet.com/api/v1/business-user/register",
    formData,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded", // ✅ Required by API
      },
    }
  );

  return response.data;
};

const BusinessAccountWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [openSuccess, setOpenSuccess] = React.useState(false);
  const [openError, setOpenError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const { setBusinessInfo } = useBusinessInfo();

  // ✅ React Query mutation for API call
  const { mutateAsync, isLoading } = useMutation(postBusinessAccount, {
    onSuccess: (data) => {
      console.log("✅ API Response:", data);
      setOpenSuccess(true);
      setBusinessInfo(data?.data);
      localStorage.setItem("access_token", data?.data.access_token);
      setTimeout(() => navigate("/business"), 2000);
    },
    onError: (error: unknown) => {
      console.error("❌ Registration Error:", error);
      const apiMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error ||
        "Something went wrong. Please try again.";
      setErrorMessage(apiMessage);
      setOpenError(true);
    },
  });

  // ✅ Pass the mutation as the onSubmit handler
  const handleSubmit = async (data: BusinessFormValues) => {
    await mutateAsync(data);
  };

  return (
    <Box position="relative">
      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.6)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Multi-Step Form */}
      <BusinessAccountMultiStepForm
        onSubmit={handleSubmit}
        loading={isLoading}
      />

      {/* ✅ Success Snackbar */}
      <Snackbar
        open={openSuccess}
        autoHideDuration={3000}
        onClose={() => setOpenSuccess(false)}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          ✅ Business account created successfully! Redirecting…
        </Alert>
      </Snackbar>

      {/* ❌ Error Snackbar */}
      <Snackbar
        open={openError}
        autoHideDuration={4000}
        onClose={() => setOpenError(false)}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          ❌ {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessAccountWrapper;
